// --- Import generatore sequenze generiche dal basso ---
import { getBottomUpFederationSequence } from './sequenceGenerator';
import { generateRoundRobinAssalti } from './roundRobinScheduler';

export const getNextFencer = (team, lastIndex, isDuo) => {
  if (team.members.length === 1) return 0;
  if (isDuo) return lastIndex === undefined ? 0 : (lastIndex + 1) % 2;

  let nextIndex = lastIndex === undefined ? 0 : (lastIndex + 1) % team.members.length;
  while (nextIndex === lastIndex) {
    nextIndex = Math.floor(Math.random() * team.members.length);
  }
  return nextIndex;
};

export const calculateBouts = (teamSize) => {
  return teamSize > 2 ? teamSize * 2 : 3;
};

// Mappa l'array dei nomi secondo il lato (rosso o verde) per staffetta
// Supporta 2vs2, 3vs3, 4vs4 e generico NvsN
// Lato: 'red' o 'green'. Se non specificato, la funzione prova a dedurlo dal contesto (default: red)
export function mapStaffettaOrder(members, lato = 'auto', assaltoSequence = []) {
  const N = members.length;
  if (N < 2) return members;

  // Se il lato è 'auto', provo a dedurlo dalla sequenza degli assalti
  let dedotto = lato;
  if (lato === 'auto' && assaltoSequence.length > 0) {
    // Prendo la colonna degli indici B (verdi)
    const indiciB = assaltoSequence.map(pair => pair[1]);
    // Se la maggioranza degli ultimi N assalti è N-1 => rosso, se 1 => verde, fallback rosso
    const countN1 = indiciB.slice(-N).filter(x => x === N - 1).length;
    const count1 = indiciB.slice(-N).filter(x => x === 1).length;
    dedotto = countN1 > count1 ? 'red' : 'green';
  }

  // Mappatura secondo la logica FIS: per i verdi si invertono le posizioni
  // Esempio: 3vs3 -> [0,2,1], 4vs4 -> [0,3,2,1], 2vs2 -> [0,1]
  let mapping = [];
  if (dedotto === 'red') {
    mapping = Array.from({ length: N }, (_, i) => i);
  } else {
    mapping = [0];
    for (let i = N - 1; i > 0; i--) mapping.push(i);
  }
  return mapping.map(idx => members[idx]);
}

// Genera la lista degli assalti (accoppiamenti) per NvsN
// Input:
//   - redNames: array dei nomi squadra rossa (ordine wizard)
//   - greenNames: array dei nomi squadra verde (ordine wizard)
//   - sequence: array di coppie di indici [[idxRed, idxGreen], ...]
//   - fromBottom: se true, compila dal basso (partendo dall'ultimo elemento della sequenza)
// Output: array di oggetti { fencerA, fencerB }
export function generateAssaltiFromSequence(redNames, greenNames, sequence, fromBottom = false) {
  const seq = fromBottom ? [...sequence].reverse() : sequence;
  return seq.map(([idxRed, idxGreen]) => ({
    fencerA: redNames[idxRed],
    fencerB: greenNames[idxGreen]
  }));
}

// Sequenza fissa per 3vs2 (nessun tiratore fa due assalti di fila)
// ROSSI: 2 tiratori, VERDI: 3 tiratori
// Restituisce la sequenza degli indici da usare con generateAssaltiFromSequence
export function getSequence3vs2() {
  // [ [rosso, verde], ... ]
  return [
    [0, 0], // a-c
    [1, 1], // b-d
    [0, 2], // a-e
    [1, 0], // b-c
    [0, 1], // a-d
    [1, 2]  // b-e
  ];
}

// Sequenza fissa per 2vs3 (nessun tiratore fa due assalti di fila)
// ROSSI: 3 tiratori, VERDI: 2 tiratori
// Restituisce la sequenza degli indici da usare con generateAssaltiFromSequence
export function getSequence2vs3() {
  // [ [rosso, verde], ... ]
  return [
    [0, 0], // a-c
    [1, 1], // b-d
    [2, 0], // c-c
    [0, 1], // a-d
    [1, 0], // b-c
    [2, 1]  // c-d
  ];
}

// Sequenza fissa per 2vs2 (nessun tiratore fa due assalti di fila)
// ROSSI: 2 tiratori, VERDI: 2 tiratori
// Restituisce la sequenza degli indici da usare con generateAssaltiFromSequence
export function getSequence2vs2() {
  // [ [rosso, verde], ... ]
  return [
    [0, 0], // a-c
    [1, 1], // b-d
    [0, 1], // a-d
    [1, 0]  // b-c
  ];
}

// Sequenza FIS ufficiale per 3vs3 staffetta classica (9 assalti)
// Ordine: 3-6, 1-5, 2-4, 1-6, 3-4, 2-5, 1-4, 2-6, 3-5
export function getSequence3vs3() {
  return [
    [2, 2], // 3-6
    [0, 1], // 1-5
    [1, 0], // 2-4
    [0, 2], // 1-6
    [2, 0], // 3-4
    [1, 1], // 2-5
    [0, 0], // 1-4
    [1, 2], // 2-6
    [2, 1]  // 3-5
  ];
}

// Sequenza FIS per 4vs4 staffetta classica (16 assalti)
// Ordine ufficiale FIS: 4-8, 1-5, 2-6, 3-7, 1-8, 4-5, 3-6, 2-7, 1-6, 2-5, 3-8, 4-7, 3-5, 2-8, 1-7, 4-6
export function getSequence4vs4() {
  return [
    [3, 3], // 4-8
    [0, 0], // 1-5
    [1, 1], // 2-6
    [2, 2], // 3-7
    [0, 3], // 1-8
    [3, 0], // 4-5
    [2, 1], // 3-6
    [1, 2], // 2-7
    [0, 1], // 1-6
    [1, 0], // 2-5
    [2, 3], // 3-8
    [3, 2], // 4-7
    [2, 0], // 3-5
    [1, 3], // 2-8
    [0, 2], // 1-7
    [3, 1]  // 4-6
  ];
}

// --- PATCH: 4vs3 e 3vs4 sequenza staffetta dal basso ---
export function getSequence4vs3() {
  // 4 rossi, 3 verdi
  // Dal basso: l'ultimo tiratore di ogni squadra fa l'ultimo assalto, poi si risale
  // Esempio: [3,2], [2,1], [1,0], [0,2], [3,1], [2,0], [1,2], [0,1], [3,0], [2,2], [1,1], [0,0]
  return [
    [3, 2],
    [2, 1],
    [1, 0],
    [0, 2],
    [3, 1],
    [2, 0],
    [1, 2],
    [0, 1],
    [3, 0],
    [2, 2],
    [1, 1],
    [0, 0]
  ];
}

export function getSequence3vs4() {
  // 3 rossi, 4 verdi
  // Dal basso: l'ultimo tiratore di ogni squadra fa l'ultimo assalto, poi si risale
  // Esempio: [2,3], [1,2], [0,1], [2,0], [1,3], [0,2], [2,1], [1,0], [0,3], [2,2], [1,1], [0,0]
  return [
    [2, 3],
    [1, 2],
    [0, 1],
    [2, 0],
    [1, 3],
    [0, 2],
    [2, 1],
    [1, 0],
    [0, 3],
    [2, 2],
    [1, 1],
    [0, 0]
  ];
}

// --- PATCH: 4vs2 e 2vs4 sequenza staffetta dal basso ---
export function getSequence4vs2() {
  // 4 rossi, 2 verdi
  // Dal basso: l'ultimo tiratore di ogni squadra fa l'ultimo assalto, poi si risale
  // Esempio: [3,1], [2,0], [1,1], [0,0], [3,0], [2,1], [1,0], [0,1]
  return [
    [3, 1],
    [2, 0],
    [1, 1],
    [0, 0],
    [3, 0],
    [2, 1],
    [1, 0],
    [0, 1]
  ];
}

export function getSequence2vs4() {
  // 2 rossi, 4 verdi
  // Dal basso: l'ultimo tiratore di ogni squadra fa l'ultimo assalto, poi si risale
  // Esempio: [1,3], [0,2], [1,1], [0,0], [1,2], [0,1], [1,0], [0,3]
  return [
    [1, 3],
    [0, 2],
    [1, 1],
    [0, 0],
    [1, 2],
    [0, 1],
    [1, 0],
    [0, 3]
  ];
}

// Restituisce la sequenza federale se esiste per la combinazione di membri
export function getFederationSequence(redCount, greenCount) {
  if (redCount === 2 && greenCount === 2) return getSequence2vs2();
  if (redCount === 3 && greenCount === 2) return getSequence3vs2();
  if (redCount === 2 && greenCount === 3) return getSequence2vs3();
  if (redCount === 3 && greenCount === 3) return getSequence3vs3();
  if (redCount === 4 && greenCount === 4) return getSequence4vs4();
  if (redCount === 4 && greenCount === 3) return getSequence4vs3();
  if (redCount === 3 && greenCount === 4) return getSequence3vs4();
  if (redCount === 4 && greenCount === 2) return getSequence4vs2();
  if (redCount === 2 && greenCount === 4) return getSequence2vs4();
  // PATCH: fallback federale dal basso per qualsiasi NxM fino a 8
  if (redCount >= 1 && greenCount >= 1 && redCount <= 8 && greenCount <= 8) {
    return getBottomUpFederationSequence(redCount, greenCount);
  }
  // Estendibile in futuro per altre combinazioni
  return null;
}

// Genera assalti secondo regole federali, se possibile. Fallback: round robin completo.
export function generateAssaltiFederali(teamRedRaw, teamGreenRaw) {
  const redObjs = Array.isArray(teamRedRaw) ? teamRedRaw.map(toNameObj) : [];
  const greenObjs = Array.isArray(teamGreenRaw) ? teamGreenRaw.map(toNameObj) : [];
  if (redObjs.length === 0 || greenObjs.length === 0) return [];

  // Verifica se è attiva la modalità zigzag (rondella) in localStorage
  const schema = localStorage.getItem('individual_schema') || 'auto';

  // Verifica se siamo in un caso in cui dobbiamo usare le sequenze FIE ufficiali
  // Caso 1: tutti contro tutti nello stesso girone (ovvero redObjs === greenObjs)
  const isAllVsAll = redObjs === greenObjs ||
    (redObjs.length === greenObjs.length &&
      redObjs.every((r, i) => r.name === greenObjs[i].name));

  // Se siamo in un girone completo (tutti contro tutti) e il numero di atleti è tra 4 e 8,
  // usiamo SEMPRE le sequenze ufficiali FIE per garantire l'ordine corretto
  if (isAllVsAll && redObjs.length >= 4 && redObjs.length <= 8) {
    console.log(`Applicando sequenza ufficiale FIE per girone da ${redObjs.length} tiratori`);
    // generateCompleteRoundRobin usa le sequenze ufficiali per gironi da 4-8 tiratori
    return generateRoundRobinAssalti(redObjs, greenObjs);
  }

  // Altrimenti, se è esplicitamente richiesto zigzag, usa l'algoritmo round-robin
  if (schema === 'zigzag') {
    console.log('Applicando algoritmo round-robin con metodo della rondella');
    return generateRoundRobinAssalti(redObjs, greenObjs);
  }

  // --- LOGICA STAFFETTA MISTA (SEPARAZIONE CATEGORIE) ---
  // Se gli atleti hanno categorie eterogenee (es. maschi/femmine), processa per sottogruppi
  const uniqueCats = [...new Set(redObjs.map(o => o.category).filter(c => c))];
  // Se troviamo più di una categoria nella squadra rossa (es. maschi e femmine)
  if (uniqueCats.length > 1) {
    console.log('[FencingLogic] Rilevata staffetta mista con categorie multiple:', uniqueCats);
    let mixedAssalti = [];

    // Ordina categorie per consistenza (es. prima femmine, poi maschi, o alfabetico)
    uniqueCats.sort().forEach(cat => {
      const subRed = redObjs.filter(r => r.category === cat);
      // Cerca nella squadra verde atleti della stessa categoria
      // (Se la squadra verde usa nomi diversi per le categorie, questo fallisce, ma assumiamo standard)
      const subGreen = greenObjs.filter(g => g.category === cat);

      if (subRed.length > 0 && subGreen.length > 0) {
        console.log(`[FencingLogic] Generazione sottogruppo per categoria '${cat}': ${subRed.length}vs${subGreen.length}`);
        // Ricorsione per generare assalti standard per questo sottogruppo (es. 2vs2)
        const partial = generateAssaltiFederali(subRed, subGreen);
        mixedAssalti = [...mixedAssalti, ...partial];
      }
    });

    // Se abbiamo generato assalti, restituiamoli. 
    // Se zero (es. categorie non corrispondenti), fallback a logica standard sotto.
    if (mixedAssalti.length > 0) return mixedAssalti;
  }

  let sequence = null;

  if (redObjs.length === 3 && greenObjs.length === 2) {
    sequence = getSequence2vs3();
  } else if (redObjs.length === 2 && greenObjs.length === 3) {
    sequence = getSequence3vs2();
  } else if (redObjs.length === 3 && greenObjs.length === 3) {
    sequence = getSequence3vs3();
  } else if (redObjs.length === 2 && greenObjs.length === 2) {
    sequence = getSequence2vs2();
  } else if (redObjs.length === 4 && greenObjs.length === 4) {
    sequence = getSequence4vs4();
  } else if (redObjs.length === 4 && greenObjs.length === 3) {
    sequence = getSequence4vs3();
  } else if (redObjs.length === 3 && greenObjs.length === 4) {
    sequence = getSequence3vs4();
  } else if (redObjs.length === 4 && greenObjs.length === 2) {
    sequence = getSequence4vs2();
  } else if (redObjs.length === 2 && greenObjs.length === 4) {
    sequence = getSequence2vs4();
  } else {
    // fallback round robin
    return redObjs.flatMap(a => greenObjs.map(b => ({ fencerA: a, fencerB: b })));
  }

  return generateAssaltiFederaliFromBottom(redObjs, greenObjs, sequence);
}

function generateAssaltiFederaliFromBottom(redNames, greenNames, sequence) {
  const redOrder = fillNamesFromBottom(sequence, redNames, 'red');
  const greenOrder = fillNamesFromBottom(sequence, greenNames, 'green');
  return sequence.map((pair, i) => ({
    fencerA: redOrder[i],
    fencerB: greenOrder[i]
  }));
}

function fillNamesFromBottom(sequence, names, lato = 'red') {
  const idxPos = lato === 'red' ? 0 : 1;
  const maxIndex = Math.max(...sequence.map(pair => pair[idxPos]));
  const resultByIndex = Array(maxIndex + 1).fill(null);
  let nameIdx = names.length - 1;

  // 1. Assegna partendo dal fondo della sequenza
  for (let i = sequence.length - 1; i >= 0 && nameIdx >= 0; i--) {
    const idx = sequence[i][idxPos];
    if (resultByIndex[idx] === null) {
      resultByIndex[idx] = names[nameIdx];
      nameIdx--;
    }
  }

  // 2. Se restano indici nulli, riempili con i tiratori non ancora usati (in ordine)
  const usati = resultByIndex.filter(x => x !== null);
  let nonUsati = names.filter(n => !usati.includes(n));
  for (let i = 0; i < resultByIndex.length; i++) {
    if (resultByIndex[i] === null && nonUsati.length > 0) {
      resultByIndex[i] = nonUsati.shift();
    }
  }

  // 3. Restituisci la lista dei nomi secondo la sequenza
  return sequence.map(pair => resultByIndex[pair[idxPos]]);
}

function toNameObj(x) {
  if (!x) return { name: '?' };
  if (typeof x === 'object' && x.name) return x;
  return { name: String(x) };
}

// Utility per generare un ID univoco (timestamp + random)
export function generateMatchId() {

  return 'match_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 6);
}

// Quando crei un nuovo match, aggiungi sempre un campo id se non esiste
export function ensureMatchHasId(match) {
  if (!match.id) {
    return { ...match, id: generateMatchId() };
  }
  return match;
}

export { fillNamesFromBottom };
